import { useEffect, useId, useRef, useState } from 'react';
import {
  cifraCorta,
  duracionLegible,
  escalaDelEje,
  etiquetaDeTramo,
  indiceDeX,
  indicesDeEtiquetas,
  puntos,
  rutaDeArea,
  rutaDeLinea,
  type Tramo,
} from '../serie';

/**
 * La actividad en el tiempo: ejecuciones, fallas, rechazos de negocio,
 * abandonos y latencia.
 *
 * Dibujado en SVG y no con una biblioteca: son líneas, un área y un cursor, y
 * el panel casi no tiene dependencias. Mide su propio ancho y calcula la caja
 * en píxeles, así el texto del eje no se estira ni se aplasta cuando cambia el
 * tamaño de la pantalla —lo que pasaría con un `viewBox` que escala todo
 * junto—.
 *
 * Cada serie se enciende y apaga: con cuatro líneas sobre el mismo eje, la de
 * ejecuciones aplasta a las demás, y quien busca un problema necesita poder
 * quitarla.
 */

type Serie = 'ejecuciones' | 'fallas' | 'negocio' | 'abandonos' | 'latencia';

interface Definicion {
  id: Serie;
  etiqueta: string;
  color: string;
  valor: (t: Tramo) => number;
  /** Se dibuja contra el eje derecho, en milisegundos. */
  derecha?: boolean;
}

const SERIES: Definicion[] = [
  { id: 'ejecuciones', etiqueta: 'Ejecuciones', color: 'var(--serie-4)', valor: (t) => t.ejecuciones },
  { id: 'fallas', etiqueta: 'Fallas', color: 'var(--mal)', valor: (t) => t.fallas },
  { id: 'negocio', etiqueta: 'De negocio', color: 'var(--texto-medio)', valor: (t) => t.negocio },
  { id: 'abandonos', etiqueta: 'Abandono', color: 'var(--aviso)', valor: (t) => t.abandonados },
  { id: 'latencia', etiqueta: 'Latencia p95', color: 'var(--serie-3)', valor: (t) => t.p95, derecha: true },
];

const POR_OMISION: Record<Serie, boolean> = {
  ejecuciones: true,
  fallas: true,
  negocio: true,
  abandonos: false,
  latencia: false,
};

/** El ancho del elemento, que cambia con la ventana y con el menú lateral. */
function useAncho(ref: React.RefObject<HTMLElement | null>, inicial: number): number {
  const [ancho, setAncho] = useState(inicial);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setAncho(Math.max(240, Math.round(el.getBoundingClientRect().width)));
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [ref]);
  return ancho;
}

export function GraficoSerie({
  tramos,
  pasoSegundos,
  ventanaDias,
  cargando,
}: {
  tramos: Tramo[];
  pasoSegundos: number;
  ventanaDias: number;
  cargando?: boolean;
}) {
  const contenedor = useRef<HTMLDivElement>(null);
  const ancho = useAncho(contenedor, 640);
  const [visibles, setVisibles] = useState<Record<Serie, boolean>>(POR_OMISION);
  const [cursor, setCursor] = useState<number | null>(null);
  const idDegradado = useId().replace(/:/g, '');

  const alto = ancho < 520 ? 220 : 280;
  const hayDerecha = visibles.latencia;
  const margen = { izq: 44, der: hayDerecha ? 52 : 14, arriba: 14, abajo: 28 };
  const caja = {
    x: margen.izq,
    y: margen.arriba,
    ancho: Math.max(60, ancho - margen.izq - margen.der),
    alto: alto - margen.arriba - margen.abajo,
  };

  const activas = SERIES.filter((s) => visibles[s.id]);
  const maxCuenta = Math.max(0, ...activas.filter((s) => !s.derecha).flatMap((s) => tramos.map(s.valor)));
  const maxLatencia = Math.max(0, ...tramos.map((t) => t.p95));
  const ejeIzq = escalaDelEje(maxCuenta);
  const ejeDer = escalaDelEje(maxLatencia);

  const etiquetas = indicesDeEtiquetas(tramos.length, ancho < 520 ? 4 : 7);
  const total = tramos.reduce((suma, t) => suma + t.ejecuciones, 0);

  function alMover(e: React.PointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setCursor(indiceDeX(e.clientX - rect.left, caja, tramos.length));
  }

  const tramoActivo = cursor !== null ? tramos[cursor] : undefined;
  const xCursor = cursor !== null ? puntos(tramos.map(() => 0), caja, 1)[cursor]?.[0] : undefined;

  return (
    <div className="grafico" ref={contenedor}>
      <div className="grafico-series" role="group" aria-label="Series visibles">
        {SERIES.map((s) => (
          <label key={s.id} className="serie-toggle">
            <input
              type="checkbox"
              checked={visibles[s.id]}
              onChange={(e) => setVisibles((v) => ({ ...v, [s.id]: e.target.checked }))}
            />
            <i className="serie-punto" style={{ background: s.color }} aria-hidden="true" />
            {s.etiqueta}
          </label>
        ))}
      </div>

      <div className={`grafico-lienzo ${cargando ? 'cargando' : ''}`}>
        <svg
          width={ancho}
          height={alto}
          viewBox={`0 0 ${ancho} ${alto}`}
          role="img"
          aria-label={`Actividad en el tiempo: ${total.toLocaleString('es-CL')} ejecuciones en ${tramos.length} tramos`}
          onPointerMove={alMover}
          onPointerDown={alMover}
          onPointerLeave={() => setCursor(null)}
          style={{ touchAction: 'pan-y' }}
        >
          <defs>
            <linearGradient id={`area-${idDegradado}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: 'var(--serie-4)', stopOpacity: 0.32 }} />
              <stop offset="100%" style={{ stopColor: 'var(--serie-4)', stopOpacity: 0 }} />
            </linearGradient>
          </defs>

          {/* Cuadrícula y eje de cuentas */}
          {ejeIzq.marcas.map((m) => {
            const y = caja.y + caja.alto - (m / ejeIzq.max) * caja.alto;
            return (
              <g key={m}>
                <line x1={caja.x} x2={caja.x + caja.ancho} y1={y} y2={y} className="grafico-rejilla" />
                <text x={caja.x - 8} y={y + 4} textAnchor="end" className="grafico-eje">
                  {cifraCorta(m)}
                </text>
              </g>
            );
          })}

          {/* Eje de latencia, solo si se muestra */}
          {hayDerecha &&
            ejeDer.marcas.map((m) => {
              const y = caja.y + caja.alto - (m / ejeDer.max) * caja.alto;
              return (
                <text key={m} x={caja.x + caja.ancho + 8} y={y + 4} className="grafico-eje">
                  {duracionLegible(m) === '—' ? '0' : duracionLegible(m)}
                </text>
              );
            })}

          {/* Eje de tiempo */}
          {etiquetas.map((i) => {
            const t = tramos[i];
            if (!t) return null;
            const x = caja.x + (tramos.length > 1 ? (i / (tramos.length - 1)) * caja.ancho : caja.ancho / 2);
            const ultimo = i === tramos.length - 1;
            return (
              <text
                key={i}
                x={x}
                y={alto - 8}
                textAnchor={i === 0 ? 'start' : ultimo ? 'end' : 'middle'}
                className="grafico-eje"
              >
                {ultimo ? 'Ahora' : etiquetaDeTramo(t.t, pasoSegundos, ventanaDias)}
              </text>
            );
          })}

          {/* Series */}
          {visibles.ejecuciones && (
            <path d={rutaDeArea(tramos.map((t) => t.ejecuciones), caja, ejeIzq.max)} fill={`url(#area-${idDegradado})`} />
          )}
          {activas.map((s) => (
            <path
              key={s.id}
              d={rutaDeLinea(tramos.map(s.valor), caja, s.derecha ? ejeDer.max : ejeIzq.max)}
              fill="none"
              style={{ stroke: s.color }}
              strokeWidth={s.id === 'ejecuciones' ? 1.8 : 1.6}
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray={s.derecha ? '4 3' : undefined}
            />
          ))}

          {/* Cursor */}
          {tramoActivo && xCursor !== undefined && (
            <g>
              <line x1={xCursor} x2={xCursor} y1={caja.y} y2={caja.y + caja.alto} className="grafico-cursor" />
              {activas.map((s) => {
                const max = s.derecha ? ejeDer.max : ejeIzq.max;
                const y = caja.y + caja.alto - (Math.min(s.valor(tramoActivo) / max, 1) || 0) * caja.alto;
                return <circle key={s.id} cx={xCursor} cy={y} r={3.5} style={{ fill: s.color }} className="grafico-punto" />;
              })}
            </g>
          )}
        </svg>

        {tramoActivo && xCursor !== undefined && (
          <div
            className="grafico-tip"
            style={{ left: Math.min(Math.max(xCursor, 90), ancho - 90) }}
            role="status"
          >
            <strong>{etiquetaDeTramo(tramoActivo.t, pasoSegundos, Math.max(ventanaDias, 2))}</strong>
            {activas.map((s) => (
              <span key={s.id}>
                <i className="serie-punto" style={{ background: s.color }} aria-hidden="true" />
                {s.etiqueta}
                <b>{s.derecha ? duracionLegible(s.valor(tramoActivo)) : s.valor(tramoActivo).toLocaleString('es-CL')}</b>
              </span>
            ))}
          </div>
        )}

        {total === 0 && !cargando && <p className="grafico-vacio">Sin ejecuciones en esta ventana.</p>}
      </div>
    </div>
  );
}
